import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';
import { COUNTRIES, countryName, dialCodeFor, flagEmoji } from '@doccareplus/shared/lib/phone';
import { FieldLabelMark } from './Input';

export interface PhoneInputProps {
  /** Selected country as an ISO 3166-1 alpha-2 code (e.g. "IN"). */
  country: string;
  onCountryChange: (iso2: string) => void;
  /** National number (digits, may contain separators). */
  value: string;
  onChange: (national: string) => void;
  label?: string;
  error?: string;
  /** Show a red "*" next to the label. */
  requiredMark?: boolean;
  /** Show a muted "(optional)" tag next to the label. Ignored when `requiredMark`. */
  optional?: boolean;
  disabled?: boolean;
  /** Locks the dial-code picker (e.g. once a country is already established) while the number stays editable. */
  disableCountry?: boolean;
  id?: string;
  placeholder?: string;
  /** Caps how many characters can be typed into the national number (e.g. 10 for India). */
  maxLength?: number;
  locale?: string;
  // User-visible strings, supplied by the caller so they can be translated.
  countryButtonLabel?: string;
  searchPlaceholder?: string;
  noResultsLabel?: string;
}

/**
 * PhoneInput — country-code picker + national number field. Pure presentational:
 * it owns no business rules and reports the country/number back to the caller, which
 * composes the E.164 value (see `phoneUtils.toE164`).
 */
export function PhoneInput({
  country,
  onCountryChange,
  value,
  onChange,
  label,
  error,
  requiredMark,
  optional,
  disabled,
  disableCountry,
  id,
  placeholder,
  maxLength,
  locale,
  countryButtonLabel = 'Select country code',
  searchPlaceholder = 'Search',
  noResultsLabel = 'No results',
}: PhoneInputProps) {
  const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-') ?? 'phone';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedDial = dialCodeFor(country) ?? '';

  const options = useMemo(
    () =>
      COUNTRIES.map((c) => ({ ...c, name: countryName(c.iso2, locale) })).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [locale]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return options;
    }
    return options.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.iso2.toLowerCase().includes(query) ||
        `+${c.dialCode}`.includes(query) ||
        c.dialCode.includes(query)
    );
  }, [options, search]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const selectCountry = (iso2: string) => {
    onCountryChange(iso2);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="flex flex-col gap-[7px]">
      {label && (
        <label htmlFor={inputId} className="block text-[12.5px] font-semibold tracking-[0.01em] text-[var(--text-2)]">
          {label}
          <FieldLabelMark requiredMark={requiredMark} optional={optional} />
        </label>
      )}

      <div className="relative" ref={containerRef} onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}>
        <div
          className={cn(
            'flex h-12 w-full items-stretch overflow-hidden rounded-[12px] border border-transparent',
            'bg-[var(--surface-2)] hover:bg-[var(--surface-3)]',
            'transition-[background-color,border-color,box-shadow] duration-200',
            'focus-within:bg-[var(--surface)] focus-within:border-[var(--primary)] focus-within:ring-4 focus-within:ring-[var(--ring)]',
            error &&
              'bg-[var(--danger-bg)] border-[var(--danger)] focus-within:bg-[var(--danger-bg)] focus-within:border-[var(--danger)] focus-within:ring-[var(--danger-bg)]'
          )}
        >
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={countryButtonLabel}
            disabled={disabled || disableCountry}
            onClick={() => setOpen((o) => !o)}
            className="flex shrink-0 items-center gap-1.5 pl-[15px] pr-2 text-[14.5px] text-[var(--text)] outline-none transition-colors hover:bg-[var(--surface-3)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span aria-hidden="true" className="text-[15px] leading-none">
              {flagEmoji(country)}
            </span>
            <span className="tabular-nums">+{selectedDial}</span>
            <svg className="h-3.5 w-3.5 text-[var(--text-3)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
            </svg>
          </button>

          <span className="my-2 w-px shrink-0 bg-[var(--border)]" aria-hidden="true" />

          <input
            id={inputId}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={(e) => onChange(e.target.value.replace(/[^\d\s()-]/g, ''))}
            className="min-w-0 flex-1 bg-transparent px-[15px] text-[14.5px] text-[var(--text)] outline-none placeholder:text-[var(--text-3)] disabled:cursor-not-allowed"
          />
        </div>

        {open && (
          <div className="absolute left-0 top-[calc(100%+4px)] z-50 w-full overflow-hidden rounded-[8px] border border-[var(--border)] bg-[var(--surface)] shadow-lg">
            <div className="p-2">
              <input
                type="text"
                value={search}
                autoFocus
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="h-9 w-full rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-3 text-[13px] text-[var(--text)] outline-none placeholder:text-[var(--text-3)] focus:border-[var(--primary)] focus:ring-[2px] focus:ring-[var(--ring)]"
              />
            </div>
            <ul className="max-h-60 overflow-auto pb-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-[13px] text-[var(--text-3)]">{noResultsLabel}</li>
              ) : (
                filtered.map((c) => (
                  <li key={c.iso2}>
                    <button
                      type="button"
                      onClick={() => selectCountry(c.iso2)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-[var(--surface-2)]',
                        c.iso2 === country && 'bg-[var(--surface-2)]'
                      )}
                    >
                      <span aria-hidden="true" className="text-[15px] leading-none">
                        {flagEmoji(c.iso2)}
                      </span>
                      <span className="flex-1 truncate text-[var(--text)]">{c.name}</span>
                      <span className="tabular-nums text-[var(--text-3)]">+{c.dialCode}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>

      {error && (
        <span className="flex items-center gap-1 text-[12px] font-medium text-[var(--danger)]">
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
          </svg>
          {error}
        </span>
      )}
    </div>
  );
}
